interface ViewerToolbarProps {
  readonly onViewFront: () => void;
  readonly onViewTop: () => void;
  readonly onViewRight: () => void;
  readonly onViewReset: () => void;
  readonly onToggleWireframe: () => void;
  readonly onToggleAutoRotate: () => void;
  readonly onScreenshot: () => void;
  readonly onToggleFullscreen: () => void;
  readonly isWireframe: boolean;
  readonly isAutoRotating: boolean;
  readonly isFullscreen: boolean;
}

export const ViewerToolbar = ({
  onViewFront,
  onViewTop,
  onViewRight,
  onViewReset,
  onToggleWireframe,
  onToggleAutoRotate,
  onScreenshot,
  onToggleFullscreen,
  isWireframe,
  isAutoRotating,
  isFullscreen,
}: ViewerToolbarProps) => (
  <div
    style={{
      position: 'absolute',
      bottom: 'var(--space-3)',
      left: '50%',
      transform: 'translateX(-50%)',
      display: 'flex',
      alignItems: 'center',
      gap: '2px',
      padding: '3px',
      borderRadius: 'var(--radius-lg)',
      backgroundColor: 'rgba(0, 0, 0, 0.55)',
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
    }}
  >
    {/* View presets */}
    <ToolBtn title="Front view" onClick={onViewFront}>F</ToolBtn>
    <ToolBtn title="Top view" onClick={onViewTop}>T</ToolBtn>
    <ToolBtn title="Right view" onClick={onViewRight}>R</ToolBtn>
    <ToolBtn title="Reset view" onClick={onViewReset}>
      <svg viewBox="0 0 20 20" fill="currentColor" style={iconStyle}>
        <path d="M10 3a7 7 0 100 14 7 7 0 000-14zm0 1.5a5.5 5.5 0 110 11 5.5 5.5 0 010-11z" />
        <circle cx="10" cy="10" r="2" />
      </svg>
    </ToolBtn>

    <Divider />

    {/* Tools */}
    <ToolBtn title="Wireframe" onClick={onToggleWireframe} active={isWireframe}>
      <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.5} style={iconStyle}>
        <rect x="3" y="3" width="14" height="14" rx="1" />
        <line x1="3" y1="10" x2="17" y2="10" />
        <line x1="10" y1="3" x2="10" y2="17" />
      </svg>
    </ToolBtn>
    <ToolBtn title="Auto-rotate" onClick={onToggleAutoRotate} active={isAutoRotating}>
      <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.5} style={iconStyle}>
        <path d="M14.5 5.5A5.5 5.5 0 004.5 10" />
        <path d="M5.5 14.5A5.5 5.5 0 0015.5 10" />
        <polyline points="4.5,7 4.5,10 7.5,10" />
        <polyline points="15.5,13 15.5,10 12.5,10" />
      </svg>
    </ToolBtn>
    <ToolBtn title="Screenshot" onClick={onScreenshot}>
      <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.5} style={iconStyle}>
        <rect x="2" y="5" width="16" height="12" rx="2" />
        <circle cx="10" cy="11" r="3" />
        <path d="M7 5l1-2h4l1 2" />
      </svg>
    </ToolBtn>
    <ToolBtn title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'} onClick={onToggleFullscreen}>
      <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.5} style={iconStyle}>
        {isFullscreen ? (
          <>
            <polyline points="7,3 7,7 3,7" />
            <polyline points="13,17 13,13 17,13" />
            <polyline points="17,7 13,7 13,3" />
            <polyline points="3,13 7,13 7,17" />
          </>
        ) : (
          <>
            <polyline points="3,7 3,3 7,3" />
            <polyline points="17,13 17,17 13,17" />
            <polyline points="13,3 17,3 17,7" />
            <polyline points="3,13 3,17 7,17" />
          </>
        )}
      </svg>
    </ToolBtn>
  </div>
);

/* ---- sub-components ---- */

const iconStyle = { width: '14px', height: '14px' };

const ToolBtn = ({
  children,
  title,
  onClick,
  active,
}: {
  children: React.ReactNode;
  title: string;
  onClick: () => void;
  active?: boolean;
}) => (
  <button
    type="button"
    title={title}
    onClick={onClick}
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '28px',
      height: '28px',
      borderRadius: 'var(--radius-md)',
      border: 'none',
      backgroundColor: active ? 'rgba(255, 255, 255, 0.25)' : 'transparent',
      color: active ? '#fff' : 'rgba(255, 255, 255, 0.75)',
      fontSize: '11px',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 150ms ease',
    }}
  >
    {children}
  </button>
);

const Divider = () => (
  <div
    style={{
      width: '1px',
      height: '18px',
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
      margin: '0 2px',
    }}
  />
);
